import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, ChevronLeft, ChevronRight, Download, Loader2, XCircle, Minus } from 'lucide-react'
import type { PredictionFilter, PredictionRow, TestRun } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import { testSetsApi, testSetsQueryKey } from '../api'

interface Props {
  testSetId: string
  run: TestRun
  classFilter: string | null
}

const FILTERS: { value: PredictionFilter; label: string }[] = [
  { value: 'ALL', label: 'Todas' },
  { value: 'CORRECT', label: 'Acertos' },
  { value: 'INCORRECT', label: 'Erros' },
  { value: 'NOT_CLASSIFIED', label: 'Não classificadas' },
  { value: 'FALSE_POSITIVE', label: 'Falsos positivos' },
  { value: 'FALSE_NEGATIVE', label: 'Falsos negativos' },
]

const PAGE_SIZE = 25

function csvCell(value: string | null): string {
  const s = value ?? ''
  // escapa aspas e envolve em aspas (cobre vírgula, quebra de linha, aspas)
  return `"${s.replace(/"/g, '""')}"`
}

function exportCsv(rows: PredictionRow[]) {
  const header = ['ID_EXTERNO', 'PROCESSO', 'TEXTO', 'ESPERADO', 'PREDITO', 'CORRETO', 'QTD_REGRAS']
  const lines = rows.map((p) =>
    [
      csvCell(p.externalId),
      csvCell(p.cnj),
      csvCell(p.text),
      csvCell(p.expectedClassName ?? p.expectedClassRaw),
      csvCell(p.predictedClassName ?? '(não classificada)'),
      csvCell(p.correct ? 'sim' : 'não'),
      csvCell(String(p.matchedRuleIds.length)),
    ].join(','),
  )
  const csv = [header.join(','), ...lines].join('\r\n')
  // BOM pra Excel reconhecer UTF-8 (acentos)
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `predicoes-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function PredictionsTab({ testSetId, run, classFilter }: Props) {
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<PredictionFilter>('ALL')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // seleção persistente entre páginas — guarda a linha inteira pra poder exportar
  const [selected, setSelected] = useState<Map<string, PredictionRow>>(new Map())

  const params = {
    page,
    pageSize: PAGE_SIZE,
    filter,
    involvingClassId: classFilter ?? undefined,
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: testSetsQueryKey.predictions(testSetId, run.id, params),
    queryFn: () => testSetsApi.predictions(testSetId, run.id, params),
  })

  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const items = data?.items ?? []

  const toggleRow = (p: PredictionRow) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(p.id)) next.delete(p.id)
      else next.set(p.id, p)
      return next
    })
  }
  const allOnPageSelected = items.length > 0 && items.every((p) => selected.has(p.id))
  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (allOnPageSelected) items.forEach((p) => next.delete(p.id))
      else items.forEach((p) => next.set(p.id, p))
      return next
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-base text-fg-1">Predições</h3>
            <p className="text-sm text-fg-3 mt-0.5">
              {total} linhas {classFilter ? '(filtrado por classe)' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selected.size > 0 && (
              <Button variant="secondary" size="sm" onClick={() => exportCsv([...selected.values()])}>
                <Download className="h-4 w-4" />
                Exportar {selected.size} caso{selected.size > 1 ? 's' : ''}
              </Button>
            )}
            <div className="flex items-center gap-1 p-0.5 rounded-md bg-bg-subtle border border-border-subtle flex-wrap">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => {
                    setFilter(f.value)
                    setPage(1)
                  }}
                  className={cn(
                    'px-2.5 h-7 text-xs font-medium rounded transition-colors',
                    filter === f.value ? 'bg-bg-surface shadow-sm text-fg-1' : 'text-fg-3 hover:text-fg-1',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-fg-3">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-fg-3 italic text-center py-10">Nenhuma linha.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-2xs uppercase tracking-wider text-fg-3 border-b border-border-subtle">
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      aria-label="Selecionar todos da página"
                      className="cursor-pointer accent-brand-600"
                    />
                  </th>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2 text-left font-semibold">ID</th>
                  <th className="px-3 py-2 text-left font-semibold">Texto</th>
                  <th className="px-3 py-2 text-left font-semibold">Esperado</th>
                  <th className="px-3 py-2 text-left font-semibold">Predito</th>
                  <th className="px-3 py-2 text-left font-semibold">Regras</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {items.map((p) => {
                  const Icon = p.correct ? CheckCircle2 : p.predictedClassId === null ? Minus : XCircle
                  const iconColor = p.correct
                    ? 'text-success'
                    : p.predictedClassId === null
                      ? 'text-fg-3'
                      : 'text-danger'
                  const expanded = expandedId === p.id
                  const isSelected = selected.has(p.id)
                  return (
                    <tr key={p.id} className={cn('hover:bg-bg-subtle', isSelected && 'bg-brand-50/50')}>
                      <td className="px-3 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(p)}
                          aria-label={`Selecionar ${p.externalId}`}
                          className="cursor-pointer accent-brand-600"
                        />
                      </td>
                      <td className="px-3 py-3 align-top">
                        <Icon className={cn('h-4 w-4', iconColor)} aria-hidden />
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="data-mono text-2xs text-fg-2">{p.externalId}</div>
                        {p.cnj && <div className="data-mono text-2xs text-fg-3">{p.cnj}</div>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className={cn('text-xs text-fg-2 max-w-md', !expanded && 'line-clamp-3')}>{p.text}</p>
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : p.id)}
                          className="text-2xs text-brand-700 hover:text-brand-800 underline mt-1"
                        >
                          {expanded ? 'recolher' : 'expandir texto'}
                        </button>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="text-xs text-fg-2">{p.expectedClassName ?? p.expectedClassRaw}</div>
                        {!p.expectedClassId && <span className="text-2xs text-fg-3 italic">não cadastrada</span>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        {p.predictedClassName ? (
                          <span className="text-xs text-fg-2">{p.predictedClassName}</span>
                        ) : (
                          <Badge tone="warning">não classif.</Badge>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className="data-mono text-2xs text-fg-3">
                          {p.matchedRuleIds.length === 0
                            ? '—'
                            : `${p.matchedRuleIds.length} regra${p.matchedRuleIds.length > 1 ? 's' : ''}`}
                        </span>
                        {p.allMatchedClassIds.length > 1 && (
                          <Badge tone="warning" className="ml-1">
                            conflito {p.allMatchedClassIds.length}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
      {data && totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-border-subtle bg-bg-subtle">
          <div className="text-xs text-fg-3">
            Página <span className="data-mono">{page}</span> de{' '}
            <span className="data-mono">{totalPages}</span> · <span className="data-mono">{total}</span> linhas
            {selected.size > 0 && (
              <>
                {' '}
                · <span className="data-mono">{selected.size}</span> selecionada{selected.size > 1 ? 's' : ''}
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
